import { useState, useEffect, useCallback } from "react";
import jsPDF from "jspdf";
import ProjectSafetyAndReceivership from "./ProjectSafetyAndReceivership";
import PlanRestructureInbox from "./PlanRestructureInbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_OPTIONS = ["Submitted", "Assessed", "Proposal Sent", "In Progress", "Turnover Complete", "Complete", "Listed", "Leased", "On Hold", "Cancelled"];

const ClientsTab = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadClients = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/clients`, { credentials: "include" });
      if (r.ok) setClients(await r.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const updateRole = async (userId, role, crewType) => {
    await fetch(`${BACKEND_URL}/api/admin/users/${userId}/role`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ role, crew_type: crewType || "" })
    });
    loadClients();
  };

  if (loading) return <div className="dash-loading">Loading clients...</div>;

  return (
    <div className="dash-tab-content" data-testid="clients-tab">
      <h2>Manage Team & Clients</h2>
      {clients.length === 0 ? (
        <div className="dash-empty">No users yet. Clients will appear here after they sign in through the portal.</div>
      ) : (
        <div className="clients-list">
          {clients.map(c => (
            <div key={c.user_id} className="client-row" data-testid={`client-${c.user_id}`}>
              <div className="client-info">
                {c.picture && <img src={c.picture} alt="" className="client-avatar" />}
                <div>
                  <strong>{c.name || "No Name"}</strong>
                  <span className="client-email">{c.email}</span>
                </div>
              </div>
              <div className="client-meta">
                <span className="client-projects">{c.project_count} project{c.project_count !== 1 ? "s" : ""}</span>
                {c.user_id === user.user_id ? (
                  <span className="role-badge role-you">You ({c.role}{c.dashboard_role ? ` / ${c.dashboard_role.toUpperCase()}` : ""})</span>
                ) : (
                  <>
                  <select
                    value={c.role || "client"}
                    onChange={e => updateRole(c.user_id, e.target.value, e.target.value === "crew" ? (c.crew_type || "handyman") : "")}
                    className="role-select"
                    data-testid={`role-select-${c.user_id}`}
                  >
                    <option value="client">Client</option>
                    <option value="crew">Crew</option>
                    <option value="team">Team</option>
                    <option value="admin">Admin</option>
                  </select>
                  {c.role === "crew" && (
                    <select
                      value={c.crew_type || "handyman"}
                      onChange={e => updateRole(c.user_id, "crew", e.target.value)}
                      className="crew-type-select"
                      data-testid={`crew-type-select-${c.user_id}`}
                    >
                      <option value="handyman">Handyman</option>
                      <option value="cleaner">Cleaner</option>
                      <option value="deep_cleaner">Deep Cleaner</option>
                      <option value="trash_out">Trash-Out Crew</option>
                      <option value="painter">Painter</option>
                      <option value="flooring">Flooring</option>
                      <option value="plumber">Plumber</option>
                      <option value="electrician">Electrician</option>
                      <option value="general">General</option>
                    </select>
                  )}
                  {c.dashboard_role && <span className="dashboard-role-badge">{c.dashboard_role.toUpperCase()}</span>}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Projects Tab =====
// ===== Receivership Section for Project Detail =====
const ReceivershipSection = ({ project, projectId, onUpdate }) => {
  const isRecv = project?.project_type === "receivership";
  const [expanded, setExpanded] = useState(isRecv);
  const [recv, setRecv] = useState(project?.receivership || {
    case_number: "", court_name: "", judge: "", receiver_name: "",
    filing_date: "", hearing_dates: [], portfolio_size: 0, notes: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project?.receivership) setRecv(project.receivership);
  }, [project]);

  const saveRecv = async () => {
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/receivership`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(recv)
      });
      if (onUpdate) onUpdate();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="pad-section recv-section" data-testid="receivership-section">
      <div className="recv-header" onClick={() => setExpanded(!expanded)}>
        <h3>{isRecv ? "Receivership Case" : "Receivership"}</h3>
        <span className="recv-toggle">{expanded ? "Collapse" : isRecv ? "Edit" : "Mark as Receivership"}</span>
      </div>
      {expanded && (
        <div className="recv-fields" data-testid="receivership-fields">
          <div className="recv-row">
            <div className="recv-field">
              <label>Case Number</label>
              <input value={recv.case_number} onChange={e => setRecv({ ...recv, case_number: e.target.value })} placeholder="e.g., CV-2026-00123" data-testid="recv-case-number" />
            </div>
            <div className="recv-field">
              <label>Court</label>
              <input value={recv.court_name} onChange={e => setRecv({ ...recv, court_name: e.target.value })} placeholder="e.g., Tarrant County 67th District" data-testid="recv-court" />
            </div>
          </div>
          <div className="recv-row">
            <div className="recv-field">
              <label>Judge</label>
              <input value={recv.judge} onChange={e => setRecv({ ...recv, judge: e.target.value })} placeholder="Judge name" data-testid="recv-judge" />
            </div>
            <div className="recv-field">
              <label>Receiver Name</label>
              <input value={recv.receiver_name} onChange={e => setRecv({ ...recv, receiver_name: e.target.value })} placeholder="Appointed receiver" data-testid="recv-receiver" />
            </div>
          </div>
          <div className="recv-row">
            <div className="recv-field">
              <label>Filing Date</label>
              <input type="date" value={recv.filing_date} onChange={e => setRecv({ ...recv, filing_date: e.target.value })} data-testid="recv-filing-date" />
            </div>
            <div className="recv-field">
              <label>Portfolio Size (units)</label>
              <input type="number" value={recv.portfolio_size} onChange={e => setRecv({ ...recv, portfolio_size: parseInt(e.target.value) || 0 })} placeholder="0" data-testid="recv-portfolio-size" />
            </div>
          </div>
          <div className="recv-field">
            <label>Court Notes</label>
            <textarea value={recv.notes} onChange={e => setRecv({ ...recv, notes: e.target.value })} placeholder="Hearing dates, special conditions, court orders..." rows="3" data-testid="recv-notes" />
          </div>
          <button onClick={saveRecv} disabled={saving} className="pad-save-btn recv-save" data-testid="recv-save-btn">
            {saving ? "Saving..." : isRecv ? "Update Receivership" : "Save as Receivership"}
          </button>
        </div>
      )}
    </div>
  );
};

const ProjectsTab = () => {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProjects = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects`, { credentials: "include" });
      if (r.ok) setProjects(await r.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  if (loading) return <div className="dash-loading">Loading projects...</div>;
  if (selected) return <ProjectDetail projectId={selected} onBack={() => { setSelected(null); loadProjects(); }} />;

  return (
    <div className="dash-tab-content" data-testid="projects-tab">
      <h2>All Projects</h2>
      <PlanRestructureInbox />
      {projects.length === 0 ? (
        <div className="dash-empty">No projects yet. Projects appear here when clients submit properties through the portal.</div>
      ) : (
        <div className="projects-admin-list">
          {projects.map(p => (
            <button key={p.project_id} className="project-admin-card" onClick={() => setSelected(p.project_id)} data-testid={`project-card-${p.project_id}`}>
              <div className="pac-top">
                <div>
                  <strong>{p.address}{p.unit_number ? ` #${p.unit_number}` : ""}</strong>
                  <span className="pac-client">{p.client_name} — {p.client_email}</span>
                </div>
                <span className={`status-badge status-${(p.status || "").toLowerCase().replace(/\s/g, "-")}`}>{p.status}</span>
              </div>
              <div className="pac-meta">
                <span>{p.city || "—"}</span>
                <span>{p.property_type || "—"}</span>
                <span>${p.monthly_rent || 0}/mo</span>
                <span>{new Date(p.created_at).toLocaleDateString()}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Project Detail (Admin) =====
const ProjectDetail = ({ projectId, onBack }) => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusVal, setStatusVal] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  // Compassion Update modal state
  const [compassionOpen, setCompassionOpen] = useState(false);
  const [compName, setCompName] = useState("");
  const [compEmail, setCompEmail] = useState("");
  const [compMessage, setCompMessage] = useState("");
  const [compSending, setCompSending] = useState(false);
  const [compStatus, setCompStatus] = useState("");
  const [compTrec, setCompTrec] = useState([]);
  const sendCompassion = async () => {
    if (!compEmail.includes("@")) { setCompStatus("Enter a valid email."); return; }
    setCompSending(true); setCompStatus(""); setCompTrec([]);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${project.project_id}/compassion-update`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipient_name: compName.trim(),
          recipient_email: compEmail.trim().toLowerCase(),
          custom_message: compMessage.trim(),
        }),
      });
      const data = await r.json();
      if (r.ok && data.ok) {
        setCompStatus(`✓ Sent — ${data.attached} photo${data.attached === 1 ? "" : "s"} attached.`);
        setTimeout(() => { setCompassionOpen(false); setCompStatus(""); setCompMessage(""); }, 2800);
      } else if (data.trec_violations) {
        setCompTrec(data.findings || []);
        setCompStatus("Blocked by TREC firewall — adjust the message below.");
      } else {
        setCompStatus(data.detail || "Send failed.");
      }
    } catch (e) {
      setCompStatus("Network error.");
    }
    setCompSending(false);
  };
  const [savingEdit, setSavingEdit] = useState(false);
  const editFieldStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    color: "#fff",
    fontSize: 13,
    boxSizing: "border-box",
  };
  const [newUpdate, setNewUpdate] = useState("");
  const [updatePhase, setUpdatePhase] = useState("");
  const [updatePhoto, setUpdatePhoto] = useState("");
  const [newDoc, setNewDoc] = useState({ name: "", url: "" });
  const [newInvoice, setNewInvoice] = useState({ description: "", amount: "", status: "Pending" });
  const [photos, setPhotos] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoType, setPhotoType] = useState("before");
  const [photoNotes, setPhotoNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [photoFilter, setPhotoFilter] = useState("all");
  const [allUsers, setAllUsers] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState([]);
  const [newMaterial, setNewMaterial] = useState({ name: "", spec: "", quantity: "" });
  const [payouts, setPayouts] = useState([]);
  const [newPayout, setNewPayout] = useState({ crew_user_id: "", rate_per_unit: "", notes: "" });
  const [markingPaid, setMarkingPaid] = useState(null);
  const [cleaningChecklist, setCleaningChecklist] = useState(null);
  const [sendingFinalPacket, setSendingFinalPacket] = useState(false);
  const [finalPacketStatus, setFinalPacketStatus] = useState("");
  const [generatingCaseStudy, setGeneratingCaseStudy] = useState(false);
  const [caseStudyStatus, setCaseStudyStatus] = useState("");
  const [publishingPortfolio, setPublishingPortfolio] = useState(false);
  const [portfolioStatus, setPortfolioStatus] = useState("");
  const [generatingCDR, setGeneratingCDR] = useState(false);
  const [cdrStatus, setCdrStatus] = useState("");

  const generateCapitalDeploymentReport = async () => {
    if (!window.confirm("Generate the CPA-Ready Capital Deployment Report? This itemizes every line by IRS asset class (Personal Property / Real Property / Operating Expenses) and includes the directional Section 168(k) Year-1 tax shield estimate. Hand directly to the owner's CPA.")) return;
    setGeneratingCDR(true);
    setCdrStatus("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/capital-deployment-report`, {
        method: "POST", credentials: "include"
      });
      if (r.ok) {
        const blob = await r.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `MF-Capital-Deployment-${projectId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setCdrStatus("Report generated and downloaded.");
      } else {
        const data = await r.json().catch(() => ({}));
        setCdrStatus(data.detail || "Failed to generate report.");
      }
    } catch (err) {
      setCdrStatus("Network error. Try again.");
    }
    setGeneratingCDR(false);
    setTimeout(() => setCdrStatus(""), 7000);
  };

  const generateCaseStudy = async () => {
    if (!window.confirm("Generate the Before/After Case Study? You'll get a branded PDF + an Instagram-ready 1080x1350 PNG.")) return;
    setGeneratingCaseStudy(true);
    setCaseStudyStatus("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/case-study`, {
        method: "POST", credentials: "include"
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) {
        // Trigger downloads
        const dl = (b64, filename, mime) => {
          const link = document.createElement("a");
          link.href = `data:${mime};base64,${b64}`;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };
        dl(data.pdf_b64, `MF-CaseStudy-${projectId}.pdf`, "application/pdf");
        setTimeout(() => dl(data.ig_png_b64, `MF-IG-${projectId}.png`, "image/png"), 500);
        setCaseStudyStatus(`Generated: PDF + IG graphic (${data.before_count} before, ${data.after_count} after photos)`);
      } else {
        setCaseStudyStatus(data.detail || "Failed to generate case study.");
      }
    } catch (err) {
      setCaseStudyStatus("Network error. Try again.");
    }
    setGeneratingCaseStudy(false);
    setTimeout(() => setCaseStudyStatus(""), 7000);
  };

  const publishToPortfolio = async () => {
    if (!window.confirm("Publish this completed project to the public Portfolio page?\n\nBefore/after photos and project details will be auto-pulled from this project.")) return;
    setPublishingPortfolio(true);
    setPortfolioStatus("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/auto-publish-portfolio`, {
        method: "POST", credentials: "include"
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) {
        if (data.already_published) {
          setPortfolioStatus("Already published. Visit /portfolio to view.");
        } else {
          setPortfolioStatus(`Published! ${data.before_count} before + ${data.after_count} after photos. Visit /portfolio.`);
        }
      } else {
        setPortfolioStatus(data.detail || "Failed to publish.");
      }
    } catch (err) {
      setPortfolioStatus("Network error. Try again.");
    }
    setPublishingPortfolio(false);
    setTimeout(() => setPortfolioStatus(""), 7000);
  };

  const sendFinalPacket = async () => {
    if (!project?.client_email) {
      setFinalPacketStatus("No client email on file for this project.");
      setTimeout(() => setFinalPacketStatus(""), 5000);
      return;
    }
    if (!window.confirm(`Email the complete Final Delivery Packet to ${project.client_email}?\n\nThis sends a full PDF record of the project: timeline, materials, photos summary, invoices, documents. You'll be BCC'd on the email.`)) {
      return;
    }
    setSendingFinalPacket(true);
    setFinalPacketStatus("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/email-final-packet`, {
        method: "POST", credentials: "include"
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setFinalPacketStatus(`Sent to ${data.sent_to}`);
        loadProject();
      } else {
        setFinalPacketStatus(data.detail || "Failed to send final packet.");
      }
    } catch (err) {
      setFinalPacketStatus("Network error. Try again.");
    }
    setSendingFinalPacket(false);
    setTimeout(() => setFinalPacketStatus(""), 7000);
  };

  const loadProject = useCallback(async () => {
    const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}`, { credentials: "include" });
    if (r.ok) {
      const data = await r.json();
      setProject(data);
      setStatusVal(data.status || "");
      setAdminNotes(data.admin_notes || "");
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadProject(); }, [loadProject]);

  const loadPhotos = useCallback(async () => {
    const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/photos`, { credentials: "include" });
    if (r.ok) setPhotos(await r.json());
  }, [projectId]);

  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  const loadPayouts = useCallback(async () => {
    const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/crew-payouts`, { credentials: "include" });
    if (r.ok) setPayouts(await r.json());
  }, [projectId]);

  useEffect(() => { loadPayouts(); }, [loadPayouts]);

  const addPayout = async () => {
    if (!newPayout.crew_user_id || !newPayout.rate_per_unit) return;
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/crew-payouts`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ ...newPayout, rate_per_unit: parseFloat(newPayout.rate_per_unit) })
    });
    setNewPayout({ crew_user_id: "", rate_per_unit: "", notes: "" });
    loadPayouts();
  };

  const togglePayoutPaid = async (crewUserId, currentStatus, method) => {
    setMarkingPaid(crewUserId);
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/crew-payouts/${crewUserId}/mark-paid`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ paid: currentStatus !== "paid", payment_method: method || "" })
    });
    await loadPayouts();
    setMarkingPaid(null);
  };

  const loadCleaningChecklist = useCallback(async () => {
    const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/cleaning-checklist`, { credentials: "include" });
    if (r.ok) setCleaningChecklist(await r.json());
  }, [projectId]);

  useEffect(() => { loadCleaningChecklist(); }, [loadCleaningChecklist]);

  const initCleaningChecklist = async () => {
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/cleaning-checklist`, { method: "POST", credentials: "include" });
    loadCleaningChecklist();
  };

  const toggleCleaningItem = async (index) => {
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/cleaning-checklist/${index}`, { method: "PUT", credentials: "include" });
    loadCleaningChecklist();
  };


  useEffect(() => {
    const loadUsers = async () => {
      const r = await fetch(`${BACKEND_URL}/api/admin/clients`, { credentials: "include" });
      if (r.ok) {
        const users = await r.json();
        setAllUsers(users.filter(u => u.role === "crew" || u.role === "team"));
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    if (project) setSelectedCrew(project.assigned_crew || []);
  }, [project]);

  const assignCrew = async () => {
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/assign-crew`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ crew_user_ids: selectedCrew })
    });
    loadProject();
  };

  const addMaterial = async () => {
    if (!newMaterial.name.trim()) return;
    const currentMaterials = project.materials || [];
    const items = [...currentMaterials.map(m => ({ name: m.name, spec: m.spec, quantity: m.quantity })), newMaterial];
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/materials`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ items })
    });
    setNewMaterial({ name: "", spec: "", quantity: "" });
    loadProject();
  };

  const uploadPhoto = async () => {
    if (!photoFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", photoFile);
    formData.append("photo_type", photoType);
    formData.append("notes", photoNotes);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/photos`, {
        method: "POST", credentials: "include", body: formData
      });
      if (r.ok) {
        setPhotoFile(null);
        setPhotoNotes("");
        loadPhotos();
      }
    } catch (e) { console.error("Upload failed:", e); }
    setUploading(false);
  };

  const getPhotoUrl = (photo) => `${BACKEND_URL}/api/files/${photo.storage_path}`;

  const deletePhoto = async (photo) => {
    const label = photo.notes || photo.photo_type || photo.original_filename || "this photo";
    const isVideo = photo.is_video || /\.(mp4|mov|webm|m4v)$/i.test(photo.original_filename || "");
    const noun = isVideo ? "video" : "photo";
    if (!window.confirm(
      `Remove this ${noun} from the project view?\n\n` +
      `"${label}"\n\n` +
      `The original is retained on file for 7-year court compliance (TUTSA / chain-of-custody). ` +
      `It will no longer appear in the dashboard, portal, packets, or triggers.`
    )) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/photos/${photo.photo_id}`, {
        method: "DELETE", credentials: "include"
      });
      if (r.ok) {
        setPhotos(prev => prev.filter(p => p.photo_id !== photo.photo_id));
      } else {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || "Could not remove. Please refresh and try again.");
      }
    } catch (e) {
      console.error("Delete photo failed:", e);
      alert("Could not remove. Please refresh and try again.");
    }
  };

  const filteredPhotos = photoFilter === "all" ? photos : photos.filter(p => p.photo_type === photoFilter);

  const saveStatus = async () => {
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ status: statusVal, admin_notes: adminNotes })
    });
    loadProject();
  };

  const openEdit = () => {
    setEditForm({
      address: project?.address || "",
      city: project?.city || "",
      unit_number: project?.unit_number || "",
      property_type: project?.property_type || "apartment",
      monthly_rent: project?.monthly_rent || 0,
      current_broker: project?.current_broker || "",
      current_broker_email: project?.current_broker_email || "",
      current_broker_phone: project?.current_broker_phone || "",
      investor_interest: project?.investor_interest || "",
      investor_notes: project?.investor_notes || "",
      demographic_notes: project?.demographic_notes || "",
      // Inner-office design intent — never surfaced to clients, courts, or tenants.
      design_intent_age: project?.design_intent_age || "",
      design_intent_for: project?.design_intent_for || "",
      design_intent_themes: project?.design_intent_themes || [],
      design_intent_notes: project?.design_intent_notes || "",
      suggested_palette: project?.suggested_palette || "",
      tags: project?.tags || [],
      current_receiver: project?.current_receiver || "",
      current_receiver_email: project?.current_receiver_email || "",
      court_order_date: project?.court_order_date || "",
    });
    setEditMode(true);
  };

  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({ new_receiver: "", new_receiver_email: "", court_order_date: "", transfer_notes: "" });
  const [transferring, setTransferring] = useState(false);

  const submitTransfer = async () => {
    if (!transferForm.new_receiver.trim()) return;
    setTransferring(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/transfer-receivership`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(transferForm)
      });
      if (r.ok) {
        setTransferModalOpen(false);
        setTransferForm({ new_receiver: "", new_receiver_email: "", court_order_date: "", transfer_notes: "" });
        loadProject();
      }
    } catch (e) { console.error("Transfer failed:", e); }
    setTransferring(false);
  };

  // Tag presets — Pilot Program / mom-and-pop FIRST, receivership grouped at the end
  const TAG_PRESETS = [
    // Pilot Program / grassroots (most common — what Shannon uses for test units)
    "pilot program", "mom-and-pop landlord", "apartment renter", "single-family rental",
    "single-room refresh", "design demo", "test pilot", "family unit",
    // Standard operating tags
    "under contract", "make-ready", "rehab", "stabilizing",
    "DV survivor", "section 8", "voucher unit", "pre-1978 lead", "compliance hold",
    // Veteran / VA / federal contract
    "veteran", "HUD-VASH", "VA voucher", "SSVF", "disabled veteran", "ADA-compliant", "trauma-informed", "foster placement",
    // Receivership / institutional (less common — only for court-appointed work)
    "new receivership", "court-supervised", "investor sniffing", "comp / test unit",
  ];
  const RECEIVERSHIP_TAGS = ["new receivership", "court-supervised", "investor sniffing"];
  const toggleTag = (t) => {
    const cur = editForm.tags || [];
    if (cur.includes(t)) setEditForm(p => ({ ...p, tags: cur.filter(x => x !== t) }));
    else setEditForm(p => ({ ...p, tags: [...cur, t] }));
  };

  // ===== Coalition Match Quick-Picks =====
  // One tap → fully configured project (tags + design intent + suggested palette).
  // Built for Shannon's mission: fast compassion at scale. Each preset honors a
  // real grassroots coalition Moovin Forward serves.
  const COALITION_PRESETS = [
    {
      id: "fup_fyi_youth",
      emoji: "🌅",
      label: "FUP / FYI Youth (18–24)",
      blurb: "Family Unification + Foster Youth Initiative — voucher youth transitioning out of foster care.",
      tags: ["pilot program", "voucher unit", "single-room refresh"],
      design_intent_age: "young_adult",
      design_intent_for: "any",
      design_intent_themes: ["minimalist", "music"],
      design_intent_notes: "FUP/FYI youth — dignity-first, neutral palette, room must feel like THEIRS not the system's.",
      suggested_palette: "whisper",
    },
    {
      id: "dv_survivor_child",
      emoji: "💜",
      label: "DV Survivor + Child",
      blurb: "Domestic violence survivor housing — mom + one child, safe-room standard.",
      tags: ["pilot program", "DV survivor", "family unit"],
      design_intent_age: "kid",
      design_intent_for: "any",
      design_intent_themes: ["pastels", "arts"],
      design_intent_notes: "DV survivor placement. Soft, healing colors. NO red accents. Privacy-first layout.",
      suggested_palette: "soft_bloom",
    },
    {
      id: "single_mom_kids",
      emoji: "🌸",
      label: "Single Mom + Kids",
      blurb: "Section 8 / voucher single-mom household with children.",
      tags: ["pilot program", "section 8", "family unit", "mom-and-pop landlord"],
      design_intent_age: "multi",
      design_intent_for: "adult_family",
      design_intent_themes: ["pastels", "arts"],
      design_intent_notes: "Mom + kids. Durable finishes. Reversible-install everywhere. Joyful palette.",
      suggested_palette: "dreamy_pastels",
    },
    {
      id: "senior_voucher",
      emoji: "🕊",
      label: "Senior on Voucher",
      blurb: "Section 202 / senior voucher — accessibility-conscious, age-in-place.",
      tags: ["pilot program", "voucher unit", "single-family rental"],
      design_intent_age: "senior",
      design_intent_for: "adult_solo",
      design_intent_themes: ["minimalist"],
      design_intent_notes: "Senior placement. High-contrast trim, slip-resistant LVP, lever handles only. Soft, calm.",
      suggested_palette: "serene_mist",
    },
    {
      id: "foster_placement_boy",
      emoji: "⚾",
      label: "Foster Placement — Boy",
      blurb: "CPS / foster youth placement, boy 5–17, fast turnaround.",
      tags: ["pilot program", "foster placement", "single-room refresh"],
      design_intent_age: "kid",
      design_intent_for: "boy",
      design_intent_themes: ["sports", "gaming"],
      design_intent_notes: "Foster placement. Must feel like home immediately. Suggest gaming OR sports based on intake.",
      suggested_palette: "adventure_cove",
    },
    {
      id: "foster_placement_girl",
      emoji: "🦋",
      label: "Foster Placement — Girl",
      blurb: "CPS / foster youth placement, girl 5–17, fast turnaround.",
      tags: ["pilot program", "foster placement", "single-room refresh"],
      design_intent_age: "kid",
      design_intent_for: "girl",
      design_intent_themes: ["pastels", "arts"],
      design_intent_notes: "Foster placement. Must feel like home immediately. Soft, dignified, NOT institutional.",
      suggested_palette: "soft_bloom",
    },
    {
      id: "hud_vash_veteran",
      emoji: "🎖",
      label: "Veteran (HUD-VASH)",
      blurb: "HUD-VASH / SSVF — honorably discharged veteran on a VA voucher.",
      tags: ["pilot program", "HUD-VASH", "veteran", "VA voucher", "single-family rental"],
      design_intent_age: "adult",
      design_intent_for: "adult_solo",
      design_intent_themes: ["minimalist", "outdoor"],
      design_intent_notes: "HUD-VASH placement. Heritage palette, dignified, NEVER military-kitsch. Quiet HVAC. Service-animal-friendly LVP. Low-glare finishes. Per 24 CFR 8 (Section 504) — accessibility-ready even if not currently disabled.",
      suggested_palette: "patriots_refuge",
    },
    {
      id: "disabled_va_veteran",
      emoji: "♿",
      label: "Disabled Veteran (VA)",
      blurb: "Disabled veteran — PTSD-aware, ADA-compliant, service-animal-ready.",
      tags: ["pilot program", "HUD-VASH", "veteran", "disabled veteran", "ADA-compliant", "trauma-informed", "VA voucher"],
      design_intent_age: "adult",
      design_intent_for: "adult_solo",
      design_intent_themes: ["minimalist"],
      design_intent_notes: "Disabled VA veteran. FULL ADA package (34in counter heights, lever handles, grab bars styled architectural-not-medical, slip-resistant LVP, no-threshold transitions, contrasting trim for low-vision). PTSD-aware: muted sage walls, no startling reflective surfaces, dimmable LED only (no bare bulbs), quiet HVAC kit, sound-dampening felt door pads. Service animal LVP. Discreet medical-equipment storage. Per 24 CFR 8 + VAWA + HUD-VASH guidelines.",
      suggested_palette: "quiet_salute",
    },
  ];

  const applyCoalition = (preset) => {
    setEditForm(p => ({
      ...p,
      tags: Array.from(new Set([...(p.tags || []), ...preset.tags])),
      design_intent_age: preset.design_intent_age,
      design_intent_for: preset.design_intent_for,
      design_intent_themes: Array.from(new Set([...(p.design_intent_themes || []), ...preset.design_intent_themes])),
      design_intent_notes: p.design_intent_notes
        ? `${p.design_intent_notes}\n\n[${preset.label}] ${preset.design_intent_notes}`
        : `[${preset.label}] ${preset.design_intent_notes}`,
      suggested_palette: preset.suggested_palette,
    }));
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = { ...editForm, monthly_rent: parseFloat(editForm.monthly_rent) || 0 };
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(payload)
      });
      if (r.ok) {
        setEditMode(false);
        loadProject();
      }
    } catch (e) { console.error("Edit save failed:", e); }
    setSavingEdit(false);
  };

  const postUpdate = async () => {
    if (!newUpdate.trim()) return;
    const payload = { text: newUpdate };
    if (updatePhase) payload.phase = updatePhase;
    if (updatePhoto) payload.image_url = updatePhoto;
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/updates`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(payload)
    });
    setNewUpdate("");
    setUpdatePhase("");
    setUpdatePhoto("");
    loadProject();
  };

  const addDocument = async () => {
    if (!newDoc.name || !newDoc.url) return;
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/documents`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(newDoc)
    });
    setNewDoc({ name: "", url: "" });
    loadProject();
  };

  const addInvoice = async () => {
    if (!newInvoice.description || !newInvoice.amount) return;
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/invoices`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ ...newInvoice, amount: parseFloat(newInvoice.amount) })
    });
    setNewInvoice({ description: "", amount: "", status: "Pending" });
    loadProject();
  };

  const createStabilizationInvoice = async () => {
    await fetch(`${BACKEND_URL}/api/admin/projects/${projectId}/create-stabilization-invoice`, {
      method: "POST", credentials: "include"
    });
    loadProject();
  };

  if (loading) return <div className="dash-loading">Loading project...</div>;
  if (!project) return <div className="dash-empty">Project not found</div>;

  return (
    <div className="project-admin-detail" data-testid="project-detail">
      <button onClick={onBack} className="admin-back-btn" data-testid="back-to-projects">&larr; All Projects</button>

      <div className="pad-header">
        <div>
          <h2>{project.address}{project.unit_number ? ` #${project.unit_number}` : ""}</h2>
          <span className="pad-client">Client: {project.client_name} ({project.client_email})</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => {
              const el = document.querySelector('[data-testid="photos-section"]');
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                const input = el.querySelector('[data-testid="photo-file-input"]');
                if (input) setTimeout(() => input.click(), 600);
              }
            }}
            data-testid="quick-upload-photos-btn"
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #E8B8C4", background: "linear-gradient(135deg, rgba(232,184,196,0.20) 0%, rgba(167,139,250,0.15) 100%)", color: "#E8B8C4", fontSize: 13, fontWeight: 800, cursor: "pointer" }}
          >
            📸 Upload Photos
          </button>
          <button
            onClick={() => setCompassionOpen(true)}
            data-testid="open-compassion-update"
            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #A78BFA", background: "linear-gradient(135deg, rgba(167,139,250,0.20) 0%, rgba(232,184,196,0.12) 100%)", color: "#E0D6FF", fontSize: 13, fontWeight: 800, cursor: "pointer" }}
            title="Send a quick parent/caseworker update email in Pilot Program voice"
          >
            💜 Compassion Update
          </button>
          <button onClick={openEdit} data-testid="edit-project-btn" style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #00FFFF", background: "rgba(0,255,255,0.10)", color: "#00FFFF", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            ✏️ Edit Project
          </button>
        </div>
      </div>

      {/* Edit Project Modal — property fields, broker, investor, demographics */}
      {editMode && (
        <div data-testid="edit-project-modal" style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.92)", backdropFilter: "blur(12px)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }}>
          <div style={{ background: "#0A1A2E", border: "1px solid rgba(0,255,255,0.25)", borderRadius: 14, padding: 24, maxWidth: 640, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <h3 style={{ color: "#fff", margin: 0, fontSize: 19 }}>✏️ Edit Project Details</h3>
              <button onClick={() => setEditMode(false)} data-testid="edit-project-close" style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 22, cursor: "pointer" }}>✕</button>
            </div>
            <p style={{ color: "#ADBED1", fontSize: 12, marginTop: 0, marginBottom: 16 }}>Every change is logged in the project's edit history (who, what, when). Use this when you spot a typo, add room details, or update tags.</p>

            {/* ===== Coalition Match Quick-Pick (1-tap project setup for grassroots coalitions) ===== */}
            <div style={{ marginBottom: 20, background: "linear-gradient(135deg, rgba(232,184,196,0.08) 0%, rgba(167,139,250,0.06) 100%)", border: "1px solid rgba(232,184,196,0.35)", borderRadius: 12, padding: "14px 16px" }} data-testid="coalition-match-panel">
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: "#E8B8C4", fontWeight: 800, letterSpacing: "0.06em" }}>💜 COALITION MATCH</span>
                <span style={{ fontSize: 10, color: "#7E92A8", fontStyle: "italic" }}>· one tap = tags + design intent + palette pre-filled</span>
              </div>
              <p style={{ fontSize: 11.5, color: "#ADBED1", margin: "0 0 12px", lineHeight: 1.5 }}>
                Who is moving into this unit? Tap the closest match — the system will fill in everything below.
                <span style={{ display: "block", marginTop: 4, fontStyle: "italic", color: "#7E92A8" }}>Optional. You can still hand-pick everything yourself.</span>
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 8 }}>
                {COALITION_PRESETS.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => applyCoalition(p)}
                    data-testid={`coalition-${p.id}`}
                    title={p.blurb}
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(232,184,196,0.3)",
                      background: "rgba(15,20,30,0.5)",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      lineHeight: 1.35,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "linear-gradient(135deg, rgba(232,184,196,0.18) 0%, rgba(167,139,250,0.12) 100%)";
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.borderColor = "rgba(232,184,196,0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(15,20,30,0.5)";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.borderColor = "rgba(232,184,196,0.3)";
                    }}
                  >
                    <div style={{ fontSize: 18, marginBottom: 4 }}>{p.emoji}</div>
                    <div style={{ color: "#E8B8C4", fontSize: 12, fontWeight: 800, marginBottom: 3 }}>{p.label}</div>
                    <div style={{ color: "#ADBED1", fontSize: 10, fontWeight: 500 }}>{p.blurb.split(" — ")[0]}</div>
                  </button>
                ))}
              </div>
              {editForm.suggested_palette && (
                <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(0,200,200,0.08)", border: "1px solid rgba(0,200,200,0.3)", borderRadius: 8, fontSize: 11.5, color: "#00C8C8" }} data-testid="coalition-palette-suggestion">
                  ✓ Coalition applied. <strong>Suggested palette: <code style={{ background: "rgba(0,200,200,0.15)", padding: "2px 6px", borderRadius: 4 }}>{editForm.suggested_palette}</code></strong> — open Design Studio to render.
                </div>
              )}
            </div>

            {/* Property fields */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: "#FFB347", fontWeight: 800, letterSpacing: "0.08em", marginBottom: 8 }}>PROPERTY</div>
              <div style={{ display: "grid", gap: 8 }}>
                <input value={editForm.address || ""} onChange={(e) => setEditForm(p => ({ ...p, address: e.target.value }))} placeholder="Street address" data-testid="edit-address" style={editFieldStyle} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <input value={editForm.city || ""} onChange={(e) => setEditForm(p => ({ ...p, city: e.target.value }))} placeholder="City, State ZIP" data-testid="edit-city" style={editFieldStyle} />
                  <input value={editForm.unit_number || ""} onChange={(e) => setEditForm(p => ({ ...p, unit_number: e.target.value }))} placeholder="Unit # (e.g. Apt 932)" data-testid="edit-unit" style={editFieldStyle} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <select value={editForm.property_type || "apartment"} onChange={(e) => setEditForm(p => ({ ...p, property_type: e.target.value }))} data-testid="edit-property-type" style={editFieldStyle}>
                    <option value="apartment">Apartment</option>
                    <option value="single_family">Single-Family</option>
                    <option value="duplex">Duplex</option>
                    <option value="multifamily">Multifamily Complex</option>
                    <option value="short_term_rental">Short-Term Rental</option>
                    <option value="commercial">Commercial</option>
                  </select>
                  <input type="number" value={editForm.monthly_rent || 0} onChange={(e) => setEditForm(p => ({ ...p, monthly_rent: e.target.value }))} placeholder="Monthly rent ($)" data-testid="edit-rent" style={editFieldStyle} />
                </div>
              </div>
            </div>

            {/* Tags */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: "#5AC8D8", fontWeight: 800, letterSpacing: "0.08em", marginBottom: 8 }}>TAGS (multi-select for filtering)</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {TAG_PRESETS.map(t => {
                  const active = (editForm.tags || []).includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`edit-tag-${t.replace(/\s+/g,"-")}`} style={{ padding: "4px 10px", borderRadius: 999, border: `1px solid ${active ? "#5AC8D8" : "rgba(255,255,255,0.15)"}`, background: active ? "rgba(90,200,216,0.20)" : "transparent", color: active ? "#5AC8D8" : "#ADBED1", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                      {active ? "✓ " : ""}{t}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Institutional-only fields — collapsed by default (Receivership / Broker / Investor).
                Auto-expands when this project actually has receivership data OR a receivership tag. */}
            {(() => {
              const hasInstitutionalData = !!(
                project?.current_receiver || editForm.current_receiver ||
                editForm.current_receiver_email || editForm.court_order_date ||
                editForm.current_broker || editForm.investor_interest
              );
              const hasReceivershipTag = (editForm.tags || []).some(t => RECEIVERSHIP_TAGS.includes(t));
              const startOpen = hasInstitutionalData || hasReceivershipTag;
              return (
            <details open={startOpen} style={{ marginBottom: 16, background: "rgba(255,179,71,0.05)", border: "1px solid rgba(255,179,71,0.2)", borderRadius: 10, padding: "10px 14px" }} data-testid="institutional-fields-toggle">
              <summary style={{ cursor: "pointer", fontSize: 11, color: "#FFB347", fontWeight: 800, letterSpacing: "0.06em", outline: "none", padding: "4px 0" }}>
                ⚖️ INSTITUTIONAL FIELDS — Receivership / Broker / Investor <span style={{ color: "#7E92A8", fontWeight: 500, letterSpacing: 0, marginLeft: 6 }}>(skip these for Pilot Program & mom-and-pop projects)</span>
              </summary>

            {/* Receivership */}
            <div style={{ marginTop: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: "#FFB347", fontWeight: 800, letterSpacing: "0.08em", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>CURRENT RECEIVER (court-appointed)</span>
                {project?.current_receiver && (
                  <button type="button" onClick={() => { setEditMode(false); setTransferModalOpen(true); }} data-testid="open-transfer-receivership" style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #FFB347", background: "transparent", color: "#FFB347", fontSize: 10, fontWeight: 800, cursor: "pointer", letterSpacing: 0 }}>
                    🔄 Transfer
                  </button>
                )}
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                <input value={editForm.current_receiver || ""} onChange={(e) => setEditForm(p => ({ ...p, current_receiver: e.target.value }))} placeholder="Receiver name (e.g. 'John Smith, Court-Appointed Receiver')" data-testid="edit-receiver" style={editFieldStyle} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <input value={editForm.current_receiver_email || ""} onChange={(e) => setEditForm(p => ({ ...p, current_receiver_email: e.target.value }))} placeholder="Receiver email" data-testid="edit-receiver-email" style={editFieldStyle} />
                  <input value={editForm.court_order_date || ""} onChange={(e) => setEditForm(p => ({ ...p, court_order_date: e.target.value }))} placeholder="Court order date (MM/DD/YYYY)" data-testid="edit-court-date" style={editFieldStyle} />
                </div>
              </div>
            </div>

            {/* Broker */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: "#A78BFA", fontWeight: 800, letterSpacing: "0.08em", marginBottom: 8 }}>CURRENT BROKER (if engaged)</div>
              <div style={{ display: "grid", gap: 8 }}>
                <input value={editForm.current_broker || ""} onChange={(e) => setEditForm(p => ({ ...p, current_broker: e.target.value }))} placeholder="Broker name & firm" data-testid="edit-broker" style={editFieldStyle} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <input value={editForm.current_broker_email || ""} onChange={(e) => setEditForm(p => ({ ...p, current_broker_email: e.target.value }))} placeholder="Broker email" data-testid="edit-broker-email" style={editFieldStyle} />
                  <input value={editForm.current_broker_phone || ""} onChange={(e) => setEditForm(p => ({ ...p, current_broker_phone: e.target.value }))} placeholder="Broker phone" data-testid="edit-broker-phone" style={editFieldStyle} />
                </div>
              </div>
            </div>

            {/* Investor */}
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, color: "#00DC78", fontWeight: 800, letterSpacing: "0.08em", marginBottom: 8 }}>INVESTOR INTEREST (track buyers sniffing)</div>
              <input value={editForm.investor_interest || ""} onChange={(e) => setEditForm(p => ({ ...p, investor_interest: e.target.value }))} placeholder="Investor / acquisition firm name" data-testid="edit-investor" style={editFieldStyle} />
              <textarea value={editForm.investor_notes || ""} onChange={(e) => setEditForm(p => ({ ...p, investor_notes: e.target.value }))} placeholder="Notes (e.g. 'Sterling Crest LOI dated 2/15, $4.2M offer pending')" rows={2} data-testid="edit-investor-notes" style={{ ...editFieldStyle, marginTop: 8, resize: "vertical", fontFamily: "inherit" }} />
            </div>
            </details>
              );
            })()}

            {/* Demographics */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, color: "#FF4D6D", fontWeight: 800, letterSpacing: "0.08em", marginBottom: 8 }}>DEMOGRAPHIC / TENANT NOTES</div>
              <textarea value={editForm.demographic_notes || ""} onChange={(e) => setEditForm(p => ({ ...p, demographic_notes: e.target.value }))} placeholder="Family makeup, accessibility needs, voucher type, transitional housing program, etc. (used to inform palette + design templates)" rows={3} data-testid="edit-demographics" style={{ ...editFieldStyle, resize: "vertical", fontFamily: "inherit" }} />
            </div>

            {/* Designed For — Inner-Office Room Staging Intent (TREC-safe: about furniture, not tenant selection) */}
            <div style={{ marginBottom: 18, background: "rgba(232,184,196,0.05)", border: "1px solid rgba(232,184,196,0.25)", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: "#E8B8C4", fontWeight: 800, letterSpacing: "0.08em" }}>💜 DESIGNED FOR (room staging intent)</span>
                <span style={{ fontSize: 9, color: "#7E92A8", fontStyle: "italic" }}>· inner-office only</span>
              </div>
              <p style={{ fontSize: 11, color: "#ADBED1", margin: "0 0 10px", lineHeight: 1.5 }}>
                Helps the system pick the right palette + furnishings (gaming chair vs. art easel, sports gear vs. dollhouse). <strong style={{ color: "#fff" }}>NEVER shown to clients, courts, tenants, or property managers.</strong> Stored privately, used only by the Design Studio and crew briefing PDFs.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <select value={editForm.design_intent_age || ""} onChange={(e) => setEditForm(p => ({ ...p, design_intent_age: e.target.value }))} data-testid="edit-design-intent-age" style={editFieldStyle}>
                  <option value="">Age range…</option>
                  <option value="toddler">Toddler (0–4)</option>
                  <option value="kid">Kid (5–9)</option>
                  <option value="tween">Tween (10–12)</option>
                  <option value="teen">Teen (13–17)</option>
                  <option value="young_adult">Young Adult (18–24)</option>
                  <option value="adult">Adult (25–54)</option>
                  <option value="senior">Senior (55+)</option>
                  <option value="multi">Multi-Generational</option>
                </select>
                <select value={editForm.design_intent_for || ""} onChange={(e) => setEditForm(p => ({ ...p, design_intent_for: e.target.value }))} data-testid="edit-design-intent-for" style={editFieldStyle}>
                  <option value="">Designed for…</option>
                  <option value="boy">Boy</option>
                  <option value="girl">Girl</option>
                  <option value="any">Any / Gender-Neutral</option>
                  <option value="adult_solo">Adult — Solo</option>
                  <option value="adult_couple">Adult — Couple</option>
                  <option value="adult_family">Adult — Family</option>
                </select>
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, color: "#E8B8C4", fontWeight: 700, marginBottom: 5 }}>Interest themes (pick all that apply)</div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {[
                    { id: "gaming", label: "🎮 Gaming / PlayStation" },
                    { id: "sports", label: "⚾ Sports" },
                    { id: "outdoor", label: "🏕 Outdoors / Nature" },
                    { id: "arts", label: "🎨 Arts / Crafts" },
                    { id: "music", label: "🎵 Music" },
                    { id: "stem", label: "🔬 STEM / Books" },
                    { id: "pastels", label: "🌸 Pastels / Florals" },
                    { id: "minimalist", label: "🤍 Minimalist" },
                    { id: "industrial", label: "🛠 Industrial / Loft" },
                  ].map(t => {
                    const active = (editForm.design_intent_themes || []).includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          const cur = editForm.design_intent_themes || [];
                          setEditForm(p => ({
                            ...p,
                            design_intent_themes: active ? cur.filter(x => x !== t.id) : [...cur, t.id]
                          }));
                        }}
                        data-testid={`edit-design-intent-theme-${t.id}`}
                        style={{
                          padding: "5px 11px",
                          borderRadius: 999,
                          border: `1px solid ${active ? "rgba(232,184,196,0.6)" : "rgba(232,184,196,0.2)"}`,
                          background: active ? "linear-gradient(135deg, #E8B8C4 0%, #A78BFA 100%)" : "rgba(232,184,196,0.05)",
                          color: active ? "#1a1a2e" : "#E8B8C4",
                          fontSize: 11,
                          fontWeight: active ? 800 : 600,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {active && "✓ "}{t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <textarea
                value={editForm.design_intent_notes || ""}
                onChange={(e) => setEditForm(p => ({ ...p, design_intent_notes: e.target.value }))}
                placeholder="Anything else? (e.g., 'Loves the Cowboys, plays Madden, has a service dog named Biscuit')"
                rows={2}
                data-testid="edit-design-intent-notes"
                style={{ ...editFieldStyle, resize: "vertical", fontFamily: "inherit", fontSize: 12 }}
              />
              <p style={{ fontSize: 10, color: "#7E92A8", fontStyle: "italic", margin: "8px 0 0", lineHeight: 1.4 }}>
                <strong style={{ color: "#E8B8C4" }}>Why this is TREC-safe:</strong> This field describes the FURNITURE we install, not the tenant. Same as any furniture store asking "is this for a child's room or a guest room?" It is not tenant screening, it is design intake.
              </p>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setEditMode(false)} data-testid="edit-project-cancel" style={{ flex: 1, padding: "11px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.10)", background: "transparent", color: "#ADBED1", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
              <button onClick={saveEdit} disabled={savingEdit} data-testid="edit-project-save" style={{ flex: 2, padding: "11px 14px", borderRadius: 8, border: "1px solid #00DC78", background: "rgba(0,220,120,0.18)", color: "#00DC78", fontSize: 13, fontWeight: 800, cursor: savingEdit ? "not-allowed" : "pointer", opacity: savingEdit ? 0.5 : 1 }}>{savingEdit ? "Saving…" : "✓ Save Changes"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Receivership Modal */}
      {compassionOpen && (
        <div data-testid="compassion-update-modal" onClick={() => !compSending && setCompassionOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.92)", backdropFilter: "blur(12px)", zIndex: 1600, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "linear-gradient(135deg, #0F1420 0%, #1A1530 100%)", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 14, padding: 22, maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <h3 style={{ color: "#E8B8C4", margin: 0, fontSize: 18, fontWeight: 800 }}>💜 Send a Compassion Update</h3>
              <button onClick={() => setCompassionOpen(false)} disabled={compSending} style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 22, cursor: "pointer" }}>✕</button>
            </div>
            <p style={{ color: "#ADBED1", fontSize: 12.5, margin: "0 0 14px", lineHeight: 1.55 }}>
              One-tap parent/caseworker update in your Pilot Program voice. We&apos;ll auto-attach the latest <strong style={{ color: "#fff" }}>2 photos</strong> from <em>{project?.address}</em> and TREC-firewall the message before send.
            </p>
            <label style={{ fontSize: 11, color: "#A78BFA", fontWeight: 700, display: "block", marginBottom: 4 }}>Send to (parent / caseworker / liaison)</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
              <input type="text" value={compName} onChange={(e) => setCompName(e.target.value)} placeholder="Name (e.g., Mrs. Rodriguez)" data-testid="compassion-name" style={{ background: "rgba(15,20,30,0.7)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 6, padding: "0.5rem 0.65rem", color: "#fff", fontSize: 13 }} />
              <input type="email" value={compEmail} onChange={(e) => setCompEmail(e.target.value)} placeholder="email@address.com" data-testid="compassion-email" style={{ background: "rgba(15,20,30,0.7)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 6, padding: "0.5rem 0.65rem", color: "#fff", fontSize: 13 }} />
            </div>
            <label style={{ fontSize: 11, color: "#A78BFA", fontWeight: 700, display: "block", marginBottom: 4 }}>Your one-line note <span style={{ color: "#7E92A8", fontWeight: 400 }}>(optional — speaks in your voice)</span></label>
            <textarea value={compMessage} onChange={(e) => setCompMessage(e.target.value)} rows={3} placeholder="e.g., Aiden's room is on track for Friday handoff. Wanted you to see the progress so far ❤️" data-testid="compassion-message" style={{ width: "100%", background: "rgba(15,20,30,0.7)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 6, padding: "0.55rem 0.7rem", color: "#fff", fontSize: 13, fontFamily: "inherit", resize: "vertical", marginBottom: 10 }} />
            {compTrec.length > 0 && (
              <div data-testid="compassion-trec" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "#ef4444", fontWeight: 700, marginBottom: 4 }}>⚠ TREC firewall — fix these words first:</div>
                {compTrec.slice(0, 4).map((f, i) => (
                  <div key={i} style={{ fontSize: 11, color: "#ADBED1", marginBottom: 2 }}>• <strong style={{ color: "#ef4444" }}>&quot;{f.trigger}&quot;</strong> → use <em style={{ color: "#10b981" }}>&quot;{f.safe_replacement}&quot;</em></div>
                ))}
              </div>
            )}
            {compStatus && (
              <p data-testid="compassion-status" style={{ fontSize: 12.5, color: compStatus.startsWith("✓") ? "#10b981" : "#ef4444", margin: "0 0 8px", fontWeight: 600 }}>{compStatus}</p>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
              <button type="button" onClick={() => setCompassionOpen(false)} disabled={compSending} style={{ background: "transparent", border: "1px solid rgba(173,190,209,0.3)", color: "#ADBED1", padding: "8px 16px", borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button type="button" onClick={sendCompassion} disabled={compSending || !compEmail.trim()} data-testid="compassion-send" style={{ background: "linear-gradient(135deg, #E8B8C4 0%, #A78BFA 100%)", border: "none", color: "#1a1a2e", padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: (compSending || !compEmail.trim()) ? "not-allowed" : "pointer", opacity: !compEmail.trim() ? 0.5 : 1 }}>
                <i className="fas fa-heart" style={{ marginRight: 5 }}></i>{compSending ? "Sending..." : "Send Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Receivership Modal */}
      {transferModalOpen && (
        <div data-testid="transfer-receivership-modal" style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.92)", backdropFilter: "blur(12px)", zIndex: 1600, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#0A1A2E", border: "1px solid rgba(255,179,71,0.30)", borderRadius: 14, padding: 22, maxWidth: 500, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <h3 style={{ color: "#fff", margin: 0, fontSize: 18 }}>🔄 Transfer Receivership</h3>
              <button onClick={() => setTransferModalOpen(false)} data-testid="transfer-close" style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 22, cursor: "pointer" }}>✕</button>
            </div>
            <p style={{ color: "#ADBED1", fontSize: 12, margin: "0 0 16px" }}>The prior receiver's info will be archived to <code style={{ color: "#FFB347" }}>receivership_history[]</code> with a timestamp. Shaneka will be auto-notified by email.</p>
            <div style={{ marginBottom: 14, padding: 10, background: "rgba(255,179,71,0.08)", borderRadius: 6, fontSize: 12, color: "#ADBED1" }}>
              <div style={{ fontSize: 10, color: "#FFB347", fontWeight: 800, letterSpacing: "0.06em", marginBottom: 4 }}>CURRENT (will be archived)</div>
              <div><strong style={{ color: "#fff" }}>{project?.current_receiver || "(none)"}</strong></div>
              {project?.current_receiver_email && <div>{project.current_receiver_email}</div>}
            </div>
            <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
              <input value={transferForm.new_receiver} onChange={(e) => setTransferForm(p => ({ ...p, new_receiver: e.target.value }))} placeholder="New receiver name (required)" data-testid="transfer-new-receiver" autoFocus style={editFieldStyle} />
              <input value={transferForm.new_receiver_email} onChange={(e) => setTransferForm(p => ({ ...p, new_receiver_email: e.target.value }))} placeholder="New receiver email" data-testid="transfer-new-email" style={editFieldStyle} />
              <input value={transferForm.court_order_date} onChange={(e) => setTransferForm(p => ({ ...p, court_order_date: e.target.value }))} placeholder="New court order date (MM/DD/YYYY)" data-testid="transfer-court-date" style={editFieldStyle} />
              <textarea value={transferForm.transfer_notes} onChange={(e) => setTransferForm(p => ({ ...p, transfer_notes: e.target.value }))} placeholder="Notes (case #, why the transfer happened, etc.)" rows={2} data-testid="transfer-notes" style={{ ...editFieldStyle, resize: "vertical", fontFamily: "inherit" }} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setTransferModalOpen(false)} data-testid="transfer-cancel" style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.10)", background: "transparent", color: "#ADBED1", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
              <button onClick={submitTransfer} disabled={transferring || !transferForm.new_receiver.trim()} data-testid="transfer-confirm" style={{ flex: 2, padding: "10px 14px", borderRadius: 8, border: "1px solid #FFB347", background: "rgba(255,179,71,0.18)", color: "#FFB347", fontSize: 13, fontWeight: 800, cursor: (transferring || !transferForm.new_receiver.trim()) ? "not-allowed" : "pointer", opacity: (transferring || !transferForm.new_receiver.trim()) ? 0.5 : 1 }}>{transferring ? "Transferring…" : "🔄 Transfer Receivership"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Stakeholder snapshot (visible on detail view) */}
      {(project.current_broker || project.investor_interest || project.demographic_notes || project.current_receiver || (project.tags && project.tags.length > 0)) && (
        <div data-testid="stakeholder-snapshot" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginBottom: 18, padding: "12px 14px", borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
          {project.tags && project.tags.length > 0 && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 9, color: "#5AC8D8", fontWeight: 800, letterSpacing: "0.08em", marginBottom: 6 }}>TAGS</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {project.tags.map(t => (
                  <span key={t} style={{ padding: "2px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: "rgba(90,200,216,0.18)", color: "#5AC8D8" }}>{t}</span>
                ))}
              </div>
            </div>
          )}
          {project.current_receiver && (
            <div>
              <div style={{ fontSize: 9, color: "#FFB347", fontWeight: 800, letterSpacing: "0.08em" }}>COURT-APPOINTED RECEIVER</div>
              <div style={{ color: "#fff", fontSize: 13, marginTop: 2 }}>{project.current_receiver}</div>
              {project.current_receiver_email && <div style={{ color: "#ADBED1", fontSize: 11 }}>{project.current_receiver_email}</div>}
              {project.court_order_date && <div style={{ color: "#ADBED1", fontSize: 11 }}>Order: {project.court_order_date}</div>}
              {project.receivership_history && project.receivership_history.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 10, color: "#7E92A8" }}>{project.receivership_history.length} prior transfer{project.receivership_history.length === 1 ? "" : "s"} archived</div>
              )}
            </div>
          )}
          {project.current_broker && (
            <div>
              <div style={{ fontSize: 9, color: "#A78BFA", fontWeight: 800, letterSpacing: "0.08em" }}>BROKER</div>
              <div style={{ color: "#fff", fontSize: 13, marginTop: 2 }}>{project.current_broker}</div>
              {project.current_broker_email && <div style={{ color: "#ADBED1", fontSize: 11 }}>{project.current_broker_email}</div>}
            </div>
          )}
          {project.investor_interest && (
            <div>
              <div style={{ fontSize: 9, color: "#00DC78", fontWeight: 800, letterSpacing: "0.08em" }}>INVESTOR</div>
              <div style={{ color: "#fff", fontSize: 13, marginTop: 2 }}>{project.investor_interest}</div>
              {project.investor_notes && <div style={{ color: "#ADBED1", fontSize: 11, marginTop: 2 }}>{project.investor_notes}</div>}
            </div>
          )}
          {project.demographic_notes && (
            <div>
              <div style={{ fontSize: 9, color: "#FF4D6D", fontWeight: 800, letterSpacing: "0.08em" }}>DEMOGRAPHICS</div>
              <div style={{ color: "#ADBED1", fontSize: 12, marginTop: 2 }}>{project.demographic_notes}</div>
            </div>
          )}
        </div>
      )}

      {/* Status & Notes */}
      {/* Inspection Photos — moved to the top of the project page so uploading is the first thing you see */}
      <div className="pad-section" data-testid="photos-section" style={{ borderColor: "rgba(232,184,196,0.35)", background: "linear-gradient(135deg, rgba(232,184,196,0.04) 0%, rgba(167,139,250,0.03) 100%)" }}>
        <h3>📸 Inspection Photos ({photos.length})</h3>
        <p className="photo-section-note">Before/after documentation for courts, brokers, and compliance records. All photos are timestamped and stored permanently.</p>
        <div className="photo-upload-row" data-testid="photo-upload">
          <label className="photo-file-label">
            {photoFile ? photoFile.name : "Choose photo..."}
            <input type="file" accept="image/*" capture="environment" onChange={e => setPhotoFile(e.target.files[0])} className="photo-file-input" data-testid="photo-file-input" />
          </label>
          <select value={photoType} onChange={e => setPhotoType(e.target.value)} className="pad-select photo-type-select" data-testid="photo-type-select">
            <option value="before">Before</option>
            <option value="after">After</option>
            <option value="progress">Progress</option>
            <option value="issue">Issue Found</option>
            <option value="issue-fixed">Issue Fixed</option>
            <option value="completed">Completed</option>
            <option value="signoff">Sign-Off</option>
          </select>
          <input value={photoNotes} onChange={e => setPhotoNotes(e.target.value)} placeholder="Documentation notes (e.g., 'Shyanne bedroom — north wall, vaulted ceiling')" className="pad-input photo-notes-input" data-testid="photo-notes-input" />
          <button onClick={uploadPhoto} disabled={!photoFile || uploading} className="pad-add-btn photo-upload-btn" data-testid="photo-upload-btn">
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>

        {photos.length > 0 && (
          <>
            <div className="photo-filter-row" data-testid="photo-filters">
              {["all", "before", "after", "progress", "issue", "signoff"].map(f => (
                <button key={f} className={`photo-filter-btn ${photoFilter === f ? "active" : ""}`} onClick={() => setPhotoFilter(f)} data-testid={`photo-filter-${f}`}>
                  {f === "all" ? `All (${photos.length})` : `${f.charAt(0).toUpperCase() + f.slice(1)} (${photos.filter(p => p.photo_type === f).length})`}
                </button>
              ))}
            </div>
            <div className="photo-grid" data-testid="photo-grid">
              {filteredPhotos.map(photo => {
                const isVideo = photo.is_video || /\.(mp4|mov|webm|m4v)$/i.test(photo.original_filename || "");
                return (
                  <div key={photo.photo_id} className="photo-card" data-testid={`photo-${photo.photo_id}`}>
                    <div className="photo-img-wrap">
                      {isVideo ? (
                        <video src={getPhotoUrl(photo)} controls preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", background: "#000" }} />
                      ) : (
                        <img src={getPhotoUrl(photo)} alt={photo.notes || photo.photo_type} loading="lazy" />
                      )}
                      <span className={`photo-type-badge badge-${photo.photo_type}`}>{photo.photo_type}{isVideo ? " · video" : ""}</span>
                      <button
                        type="button"
                        onClick={() => deletePhoto(photo)}
                        className="photo-delete-btn"
                        title="Remove from project view (original retained for 7-yr compliance)"
                        aria-label="Remove photo"
                        data-testid={`photo-delete-${photo.photo_id}`}
                        style={{
                          position: "absolute", top: 8, right: 8, width: 32, height: 32,
                          borderRadius: "50%", border: "none", cursor: "pointer",
                          background: "rgba(15, 23, 42, 0.78)", color: "#fff",
                          fontSize: 16, lineHeight: 1, display: "flex",
                          alignItems: "center", justifyContent: "center",
                          backdropFilter: "blur(6px)",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                          transition: "background 0.15s ease, transform 0.15s ease",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(220, 38, 38, 0.9)"; e.currentTarget.style.transform = "scale(1.08)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(15, 23, 42, 0.78)"; e.currentTarget.style.transform = "scale(1)"; }}
                      >
                        🗑
                      </button>
                    </div>
                    <div className="photo-meta">
                      {photo.notes && <p className="photo-notes">{photo.notes}</p>}
                      <span className="photo-date">{new Date(photo.created_at).toLocaleString()} — {photo.uploaded_by}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="pad-section" data-testid="status-section">
        <h3>Status & Notes</h3>
        <div className="pad-row">
          <select value={statusVal} onChange={e => setStatusVal(e.target.value)} className="pad-select" data-testid="status-select">
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={saveStatus} className="pad-save-btn" data-testid="save-status-btn">Save</button>
        </div>
        <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Internal team notes (visible to client)..." className="pad-textarea" data-testid="admin-notes-input" />
      </div>

      {/* Receivership Data */}
      <ReceivershipSection project={project} projectId={projectId} onUpdate={loadProject} />

      {/* Crew Assignment */}
      <div className="pad-section" data-testid="crew-section">
        <h3>Crew Assignment</h3>
        <p className="crew-assign-note">Assign crew members who can upload photos and verify materials from the /crew page.</p>
        <div className="pad-add-row pad-inline">
          <select multiple value={selectedCrew} onChange={e => setSelectedCrew(Array.from(e.target.selectedOptions, o => o.value))} className="pad-select crew-multi-select" data-testid="crew-select">
            {allUsers.map(u => (
              <option key={u.user_id} value={u.user_id}>{u.name} ({u.email}) — {u.role}</option>
            ))}
          </select>
          <button onClick={assignCrew} className="pad-save-btn" data-testid="assign-crew-btn">Assign</button>
        </div>
        {project.crew_members?.length > 0 && (
          <div className="crew-assigned-list">
            {project.crew_members.map(c => (
              <span key={c.user_id} className="crew-assigned-tag">{c.name} <span className={`crew-type-badge ctype-${c.crew_type || "general"}`}>{c.crew_type || "general"}</span></span>
            ))}
          </div>
        )}
      </div>

      {/* Crew Payroll */}
      <div className="pad-section" data-testid="payroll-section">
        <h3>Crew Payroll ({payouts.length})</h3>
        <p className="crew-assign-note">Set per-unit payout rates for assigned crew. Track payments for 1099 reporting.</p>

        {/* Profit Margin Summary */}
        {payouts.length > 0 && (() => {
          const totalLabor = payouts.reduce((s, p) => s + (p.rate_per_unit || 0), 0);
          const totalPaid = payouts.filter(p => p.status === "paid").reduce((s, p) => s + (p.rate_per_unit || 0), 0);
          const totalOwed = totalLabor - totalPaid;
          const stabFee = project.invoices?.find(i => i.description?.toLowerCase().includes("stabilization"))?.amount || 0;
          const margin = stabFee > 0 ? stabFee - totalLabor : 0;
          return (
            <div className="payroll-summary-bar" data-testid="payroll-summary">
              <div className="payroll-stat">
                <span className="payroll-stat-label">Total Labor</span>
                <span className="payroll-stat-value">${totalLabor.toFixed(2)}</span>
              </div>
              <div className="payroll-stat">
                <span className="payroll-stat-label">Paid Out</span>
                <span className="payroll-stat-value payroll-paid">${totalPaid.toFixed(2)}</span>
              </div>
              <div className="payroll-stat">
                <span className="payroll-stat-label">Owed</span>
                <span className="payroll-stat-value payroll-owed">${totalOwed.toFixed(2)}</span>
              </div>
              {stabFee > 0 && (
                <>
                  <div className="payroll-stat">
                    <span className="payroll-stat-label">Stab. Fee</span>
                    <span className="payroll-stat-value">${stabFee.toFixed(2)}</span>
                  </div>
                  <div className="payroll-stat">
                    <span className="payroll-stat-label">Margin</span>
                    <span className={`payroll-stat-value ${margin >= 0 ? "payroll-paid" : "payroll-owed"}`}>${margin.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Add Payout */}
        <div className="pad-add-row pad-inline payroll-add-row">
          <select value={newPayout.crew_user_id} onChange={e => setNewPayout(p => ({...p, crew_user_id: e.target.value}))} className="pad-select" data-testid="payout-crew-select">
            <option value="">Select crew member</option>
            {(project.crew_members || []).map(c => (
              <option key={c.user_id} value={c.user_id}>{c.name}</option>
            ))}
          </select>
          <input value={newPayout.rate_per_unit} onChange={e => setNewPayout(p => ({...p, rate_per_unit: e.target.value}))} placeholder="Rate ($)" type="number" className="pad-input pad-input-sm" data-testid="payout-rate-input" />
          <input value={newPayout.notes} onChange={e => setNewPayout(p => ({...p, notes: e.target.value}))} placeholder="Notes (e.g., Paint — 2BR)" className="pad-input" data-testid="payout-notes-input" />
          <button onClick={addPayout} className="pad-add-btn" data-testid="add-payout-btn">Add</button>
        </div>

        {/* Payout List */}
        {payouts.length > 0 && (
          <div className="payroll-list" data-testid="payroll-list">
            {payouts.map(p => (
              <div key={p.crew_user_id} className={`payroll-row ${p.status === "paid" ? "payroll-row-paid" : ""}`} data-testid={`payout-${p.crew_user_id}`}>
                <div className="payroll-row-info">
                  <strong>{p.crew_name || p.crew_user_id}</strong>
                  {p.crew_email && <span className="payroll-email">{p.crew_email}</span>}
                  {p.notes && <span className="payroll-notes">{p.notes}</span>}
                </div>
                <div className="payroll-row-amount">
                  <span className="payroll-rate">${(p.rate_per_unit || 0).toFixed(2)}</span>
                  <span className={`payroll-status-badge ${p.status}`}>{p.status === "paid" ? "Paid" : "Pending"}</span>
                  {p.paid_at && <span className="payroll-paid-date">{new Date(p.paid_at).toLocaleDateString()}</span>}
                </div>
                <div className="payroll-row-actions">
                  {p.status !== "paid" ? (
                    <>
                      <button onClick={() => togglePayoutPaid(p.crew_user_id, p.status, "zelle")} disabled={markingPaid === p.crew_user_id} className="payroll-pay-btn" data-testid={`pay-zelle-${p.crew_user_id}`}>Zelle</button>
                      <button onClick={() => togglePayoutPaid(p.crew_user_id, p.status, "cash")} disabled={markingPaid === p.crew_user_id} className="payroll-pay-btn" data-testid={`pay-cash-${p.crew_user_id}`}>Cash</button>
                      <button onClick={() => togglePayoutPaid(p.crew_user_id, p.status, "check")} disabled={markingPaid === p.crew_user_id} className="payroll-pay-btn" data-testid={`pay-check-${p.crew_user_id}`}>Check</button>
                    </>
                  ) : (
                    <button onClick={() => togglePayoutPaid(p.crew_user_id, p.status, "")} className="payroll-undo-btn" data-testid={`undo-pay-${p.crew_user_id}`}>Undo</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Materials Checklist */}
      <div className="pad-section" data-testid="materials-section">
        <h3>Materials Checklist ({(project.materials || []).filter(m => m.checked).length}/{(project.materials || []).length})</h3>
        <p className="crew-assign-note">Define materials for this project. Crew members verify on-site from the /crew page.</p>
        <div className="pad-add-row pad-inline materials-add-row">
          <input value={newMaterial.name} onChange={e => setNewMaterial(p => ({...p, name: e.target.value}))} placeholder="Material name (e.g., LVP Flooring)" className="pad-input" data-testid="material-name-input" />
          <input value={newMaterial.spec} onChange={e => setNewMaterial(p => ({...p, spec: e.target.value}))} placeholder="Spec (e.g., Lifeproof Sterling Oak)" className="pad-input" data-testid="material-spec-input" />
          <input value={newMaterial.quantity} onChange={e => setNewMaterial(p => ({...p, quantity: e.target.value}))} placeholder="Qty" className="pad-input material-qty-input" data-testid="material-qty-input" />
          <button onClick={addMaterial} className="pad-add-btn" data-testid="add-material-btn">Add</button>
        </div>
        {(project.materials || []).length > 0 && (
          <div className="materials-list-admin">
            {project.materials.map((m, i) => (
              <div key={i} className={`material-row ${m.checked ? "checked" : ""}`}>
                <span className={`material-check ${m.checked ? "on" : ""}`}>{m.checked ? "\u2713" : ""}</span>
                <span className="material-name">{m.name}</span>
                {m.spec && <span className="material-spec">{m.spec}</span>}
                {m.quantity && <span className="material-qty">x{m.quantity}</span>}
                {m.checked && m.checked_by && <span className="material-checked-by">{m.checked_by}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cleaning Checklist */}
      <div className="pad-section" data-testid="cleaning-section">
        <h3>Cleaning Checklist {cleaningChecklist?.items?.length > 0 ? `(${cleaningChecklist.items.filter(i => i.checked).length}/${cleaningChecklist.items.length})` : ""}</h3>
        <p className="crew-assign-note">Deep cleaning checklist for 1099 cleaners. Initialize to load the standard template, then assign crew to check off items on-site.</p>
        {!cleaningChecklist?.items?.length ? (
          <button onClick={initCleaningChecklist} className="pad-add-btn cleaning-init-btn" data-testid="init-cleaning-btn">Initialize Cleaning Checklist</button>
        ) : (
          <div className="cleaning-checklist-list" data-testid="cleaning-checklist-list">
            {["Kitchen", "Bathrooms", "Floors", "General", "Final"].map(cat => {
              const catItems = cleaningChecklist.items.map((item, idx) => ({...item, _idx: idx})).filter(i => i.category === cat);
              if (catItems.length === 0) return null;
              const catDone = catItems.filter(i => i.checked).length;
              return (
                <div key={cat} className="cleaning-category" data-testid={`cleaning-cat-${cat.toLowerCase()}`}>
                  <div className="cleaning-cat-header">
                    <span className="cleaning-cat-name">{cat}</span>
                    <span className="cleaning-cat-count">{catDone}/{catItems.length}</span>
                  </div>
                  {catItems.map(item => (
                    <div key={item._idx} className={`cleaning-item ${item.checked ? "cleaning-item-done" : ""}`}>
                      <button onClick={() => toggleCleaningItem(item._idx)} className={`cleaning-check ${item.checked ? "on" : ""}`} data-testid={`cleaning-toggle-${item._idx}`}>
                        {item.checked ? "\u2713" : ""}
                      </button>
                      <span className="cleaning-item-text">{item.item}</span>
                      {item.checked && item.checked_by && <span className="cleaning-checked-by">{item.checked_by}</span>}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Post Update */}
      <div className="pad-section" data-testid="updates-section">
        <h3>Updates ({project.updates?.length || 0})</h3>
        <div className="pad-add-row">
          <div className="update-form-row">
            <select value={updatePhase} onChange={e => setUpdatePhase(e.target.value)} className="pad-select phase-select" data-testid="phase-select">
              <option value="">No phase change</option>
              <option value="intake">Intake — Walk-through & scope</option>
              <option value="materials">Materials — Procurement & staging</option>
              <option value="execution">Execution — Active stabilization</option>
              <option value="signoff">Sign-Off — Walk-through & approval</option>
              <option value="complete">Complete — Delivered</option>
            </select>
          </div>
          <textarea value={newUpdate} onChange={e => setNewUpdate(e.target.value)} placeholder="Post an update for the client (they'll see this in their portal)..." className="pad-textarea" data-testid="new-update-input" />
          <input value={updatePhoto} onChange={e => setUpdatePhoto(e.target.value)} placeholder="Photo URL (optional — progress photo link)" className="pad-input" data-testid="update-photo-input" />
          <button onClick={postUpdate} className="pad-add-btn" data-testid="post-update-btn">Post Update</button>
        </div>
        {project.updates?.map(u => (
          <div key={u.update_id} className="pad-item">
            <div className="pad-item-header">
              <span className="pad-item-date">{new Date(u.created_at).toLocaleString()} — {u.posted_by}</span>
              {u.phase && <span className="pad-phase-badge">{u.phase}</span>}
            </div>
            <p>{u.text}</p>
            {u.image_url && <img src={u.image_url} alt="Progress" className="pad-update-photo" />}
          </div>
        ))}
      </div>

      {/* Add Document */}
      <div className="pad-section" data-testid="documents-section">
        <h3>Documents ({project.documents?.length || 0})</h3>
        <div className="pad-add-row pad-inline">
          <input value={newDoc.name} onChange={e => setNewDoc(p => ({...p, name: e.target.value}))} placeholder="Document name" className="pad-input" data-testid="doc-name-input" />
          <input value={newDoc.url} onChange={e => setNewDoc(p => ({...p, url: e.target.value}))} placeholder="Document URL" className="pad-input" data-testid="doc-url-input" />
          <button onClick={addDocument} className="pad-add-btn" data-testid="add-doc-btn">Add</button>
        </div>
        {project.documents?.map(d => (
          <div key={d.document_id} className="pad-item pad-doc-item">
            <a href={d.url} target="_blank" rel="noopener noreferrer">{d.name}</a>
            <span className="pad-item-date">{new Date(d.created_at).toLocaleDateString()}</span>
          </div>
        ))}
      </div>

      {/* Add Invoice */}
      <div className="pad-section" data-testid="invoices-section">
        <h3>Invoices ({project.invoices?.length || 0})</h3>
        <div className="pad-add-row pad-inline">
          <input value={newInvoice.description} onChange={e => setNewInvoice(p => ({...p, description: e.target.value}))} placeholder="Description" className="pad-input" data-testid="invoice-desc-input" />
          <input value={newInvoice.amount} onChange={e => setNewInvoice(p => ({...p, amount: e.target.value}))} placeholder="Amount" type="number" className="pad-input pad-input-sm" data-testid="invoice-amount-input" />
          <select value={newInvoice.status} onChange={e => setNewInvoice(p => ({...p, status: e.target.value}))} className="pad-select-sm" data-testid="invoice-status-select">
            <option value="Pending">Pending</option>
            <option value="Sent">Sent</option>
            <option value="Paid">Paid</option>
            <option value="Overdue">Overdue</option>
          </select>
          <button onClick={addInvoice} className="pad-add-btn" data-testid="add-invoice-btn">Add</button>
        </div>
        {project.phase === "signoff" && (
          <button onClick={createStabilizationInvoice} className="pad-save-btn stab-invoice-btn" data-testid="create-stab-invoice-btn">
            Auto-Generate Stabilization Fee Invoice (Rent + 15%)
          </button>
        )}
        {project.invoices?.map(inv => (
          <div key={inv.invoice_id} className="pad-item pad-invoice-item">
            <div><strong>{inv.description}</strong><span className="pad-item-date">{new Date(inv.created_at).toLocaleDateString()}</span></div>
            <div className="pad-invoice-right">
              <span className={`inv-status inv-${inv.status.toLowerCase()}`}>{inv.status}</span>
              <span className="inv-amount">${inv.amount.toFixed(2)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Admin Next Actions Checklist */}
      <div className="pad-section next-actions-section" data-testid="next-actions-section">
        <h3>Admin Action Checklist</h3>
        <p className="next-actions-note">Phase-specific next steps. Follow in order for each project.</p>
        {(!project.phase || project.phase === "intake") && (
          <div className="action-checklist" data-testid="actions-intake">
            <h4 className="action-phase-label">Phase: Intake</h4>
            <div className="action-item">Walk the property — document condition with before photos</div>
            <div className="action-item">Create scope of work in Team Notes (admin notes)</div>
            <div className="action-item">Define materials list (Materials section above)</div>
            <div className="action-item">Create Activation Capital invoice (e.g., "Activation Capital — [Address]")</div>
            <div className="action-item">Assign crew members to the project</div>
            <div className="action-item">Client pays Activation Capital → phase auto-advances to Procurement</div>
          </div>
        )}
        {project.phase === "materials" && (
          <div className="action-checklist" data-testid="actions-materials">
            <h4 className="action-phase-label">Phase: 48-Hr Procurement</h4>
            <div className="action-item">Verify payment settled in Stripe Dashboard (stripe.com)</div>
            <div className="action-item">Purchase all materials from approved vendors (Sherwin-Williams, HD Supply, Ferguson)</div>
            <div className="action-item">Save ALL material receipts — these go into the Line-Itemized Packet</div>
            <div className="action-item">Stage materials at property or staging location</div>
            <div className="action-item">Confirm crew schedule and set execution start date</div>
            <div className="action-item">Notify client: "Materials secured. Crew mobilizing [date]."</div>
            <div className="action-item">Advance phase to Execution when crew is on-site</div>
          </div>
        )}
        {project.phase === "execution" && (
          <div className="action-checklist" data-testid="actions-execution">
            <h4 className="action-phase-label">Phase: Execution</h4>
            <div className="action-item">Crew uploads daily progress photos with notes</div>
            <div className="action-item">Document any issues found (crew flags with "Issue Found" photos)</div>
            <div className="action-item">Communicate with client if scope changes are needed</div>
            <div className="action-item">Verify materials checklist — crew checks off items on-site</div>
            <div className="action-item">Post progress updates for client to see in portal</div>
            <div className="action-item">When work is complete: crew uploads "Completed" photos</div>
            <div className="action-item">Advance phase to Sign-Off and schedule client walk-through</div>
          </div>
        )}
        {project.phase === "signoff" && (
          <div className="action-checklist" data-testid="actions-signoff">
            <h4 className="action-phase-label">Phase: Sign-Off</h4>
            <div className="action-item">Schedule client walk-through</div>
            <div className="action-item">Crew uploads final "Sign-Off" documentation photos</div>
            <div className="action-item">Compile Line-Itemized Packet (all receipts, before/after, NSPIRE checklist)</div>
            <div className="action-item">Upload Line-Itemized Packet to Documents section</div>
            <div className="action-item">Click "Auto-Generate Stabilization Fee Invoice" button above</div>
            <div className="action-item">Client pays Stabilization Fee via portal → advance to Complete</div>
            <div className="action-item">Collect sign-off acknowledgment — save to documents</div>
          </div>
        )}
        {project.phase === "complete" && (
          <div className="action-checklist" data-testid="actions-complete">
            <h4 className="action-phase-label action-complete">Phase: Complete</h4>
            <div className="action-item">Verify Stabilization Fee payment settled</div>
            <div className="action-item">Deliver final Line-Itemized Packet to client</div>
            <div className="action-item">Request testimonial / review for portfolio</div>
            <div className="action-item">Add project to completed portfolio (before/after showcase)</div>
            <div className="action-item">Follow up in 30 days — ask about next units</div>
            <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(13,148,136,0.08)", border: "1px solid rgba(13,148,136,0.35)", borderRadius: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 20 }}>📦</span>
                <div style={{ flex: 1, minWidth: 220 }} className="mf-training-tip">
                  <strong style={{ color: "#0d9488", fontSize: 13 }}>One-Click Final Delivery Packet</strong>
                  <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.4, marginTop: 3 }}>
                    Emails the client the complete project record PDF: timeline, materials, photo summary, invoices, documents. BCC to you for records.
                    {project.final_packet_sent_at && (
                      <span style={{ color: "#0d9488", display: "block", marginTop: 3 }}>
                        ✓ Last sent: {new Date(project.final_packet_sent_at).toLocaleString()} to {project.final_packet_sent_to || project.client_email}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={sendFinalPacket}
                  disabled={sendingFinalPacket}
                  data-testid="btn-send-final-packet"
                  style={{ background: "#0d9488", color: "#fff", border: "none", padding: "10px 18px", borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: sendingFinalPacket ? "wait" : "pointer", opacity: sendingFinalPacket ? 0.6 : 1 }}
                >
                  {sendingFinalPacket ? "Sending…" : (project.final_packet_sent_at ? "Resend Packet" : "Email Final Packet")}
                </button>
              </div>
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed rgba(13,148,136,0.25)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 11, color: "#64748b", fontStyle: "italic" }}>
                  Want to see what the client will receive first?
                </span>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <a
                    href={`${BACKEND_URL}/api/admin/projects/${project.project_id}/preview-packet`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="btn-preview-real-packet"
                    style={{ background: "#0d9488", color: "#fff", border: "none", padding: "6px 14px", borderRadius: 6, fontWeight: 700, fontSize: 12, textDecoration: "none", whiteSpace: "nowrap" }}
                    title="Preview the EXACT packet that will be emailed for THIS project."
                  >
                    📄 Preview This Project's Packet
                  </a>
                  <a
                    href={`${BACKEND_URL}/api/admin/projects/sample-packet`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="btn-preview-sample-packet"
                    style={{ background: "transparent", color: "#0d9488", border: "1px solid #0d9488", padding: "6px 14px", borderRadius: 6, fontWeight: 700, fontSize: 12, textDecoration: "none", whiteSpace: "nowrap" }}
                    title="Preview a generic sample packet using placeholder data."
                  >
                    Sample (placeholder)
                  </a>
                </div>
              </div>
              {finalPacketStatus && (
                <p data-testid="final-packet-status" style={{ margin: "8px 0 0", fontSize: 12, fontWeight: 600, color: finalPacketStatus.startsWith("Sent") ? "#10b981" : "#ef4444" }}>
                  {finalPacketStatus}
                </p>
              )}
            </div>

            {/* Marketing & Portfolio Tools */}
            <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                <span style={{ fontSize: 20 }}>📸</span>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <strong style={{ color: "#A78BFA", fontSize: 13 }}>Marketing, Portfolio &amp; CPA Tools</strong>
                  <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.4, marginTop: 3 }}>
                    Generate a Before/After Case Study (PDF + IG-ready 1080×1350), auto-publish to the public Portfolio, or export a CPA-ready Capital Deployment Report itemized by IRS asset class.
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={generateCaseStudy}
                  disabled={generatingCaseStudy}
                  data-testid="btn-generate-case-study"
                  style={{ background: "#A78BFA", color: "#1a0a3e", border: "none", padding: "9px 16px", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: generatingCaseStudy ? "wait" : "pointer", opacity: generatingCaseStudy ? 0.6 : 1 }}
                >
                  {generatingCaseStudy ? "Generating…" : "🎨 Generate Case Study"}
                </button>
                <button
                  onClick={publishToPortfolio}
                  disabled={publishingPortfolio}
                  data-testid="btn-publish-portfolio"
                  style={{ background: "rgba(0,255,255,0.1)", color: "#00FFFF", border: "1px solid rgba(0,255,255,0.4)", padding: "9px 16px", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: publishingPortfolio ? "wait" : "pointer", opacity: publishingPortfolio ? 0.6 : 1 }}
                >
                  {publishingPortfolio ? "Publishing…" : "🌐 Publish to Portfolio"}
                </button>
                <button
                  onClick={generateCapitalDeploymentReport}
                  disabled={generatingCDR}
                  data-testid="btn-generate-cdr"
                  style={{ background: "rgba(0,220,120,0.12)", color: "#00DC78", border: "1px solid rgba(0,220,120,0.45)", padding: "9px 16px", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: generatingCDR ? "wait" : "pointer", opacity: generatingCDR ? 0.6 : 1 }}
                  title="CPA-Ready: itemizes by IRS asset class with Sec 168(k) Year-1 estimate"
                >
                  {generatingCDR ? "Generating…" : "📊 CPA Audit-Ready Export"}
                </button>
              </div>
              {caseStudyStatus && (
                <p data-testid="case-study-status" style={{ margin: "8px 0 0", fontSize: 11, fontWeight: 600, color: caseStudyStatus.startsWith("Generated") ? "#10b981" : "#ef4444" }}>
                  {caseStudyStatus}
                </p>
              )}
              {portfolioStatus && (
                <p data-testid="portfolio-status" style={{ margin: "4px 0 0", fontSize: 11, fontWeight: 600, color: portfolioStatus.startsWith("Published") || portfolioStatus.startsWith("Already") ? "#10b981" : "#ef4444" }}>
                  {portfolioStatus}
                </p>
              )}
              {cdrStatus && (
                <p data-testid="cdr-status" style={{ margin: "4px 0 0", fontSize: 11, fontWeight: 600, color: cdrStatus.startsWith("Report") ? "#10b981" : "#ef4444" }}>
                  {cdrStatus}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Crew Safety Hold + Receivership PDF panel — Jim's wiring spec */}
        <ProjectSafetyAndReceivership
          projectId={projectId}
          projectAddress={project?.address || ""}
        />
      </div>
    </div>
  );
};


export { ClientsTab };
export default ProjectsTab;
